import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { useMobileShellViewport } from "@/hooks/useMobileShellViewport";
import type { ToastDismissReason, ToastRecord } from "@/lib/toastStore";
import { cn } from "@/lib/utils";
import "./toast.css";

interface ToastViewportProps {
  toasts: ToastRecord[];
  dismiss: (id: string, reason?: ToastDismissReason) => void;
  pause: (id: string) => void;
  resume: (id: string) => void;
}

export function ToastViewport({
  toasts,
  dismiss,
  pause,
  resume,
}: ToastViewportProps) {
  const isMobile = useMobileShellViewport();

  if (toasts.length === 0 || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        "app-toast-viewport",
        isMobile ? "app-toast-viewport--mobile" : "app-toast-viewport--desktop",
      )}
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="app-toast glass-panel-strong"
          role="status"
          style={
            toast.durationMs > 0
              ? ({
                  "--toast-duration": `${toast.durationMs}ms`,
                } as CSSProperties)
              : undefined
          }
          onMouseEnter={() => pause(toast.id)}
          onMouseLeave={() => resume(toast.id)}
        >
          <div className="app-toast__body">
            <p className="app-toast__title">{toast.title}</p>
            {toast.description ? (
              <p className="app-toast__description">{toast.description}</p>
            ) : null}
          </div>
          {toast.action ? (
            <button
              type="button"
              className="app-toast__action"
              onClick={() => dismiss(toast.id, "action")}
            >
              {toast.action.label}
            </button>
          ) : null}
          <button
            type="button"
            className="app-toast__dismiss"
            aria-label="Dismiss notification"
            onClick={() => dismiss(toast.id, "dismiss")}
          >
            <X className="size-3.5" />
          </button>
          {toast.durationMs > 0 ? (
            <span className="app-toast__progress" aria-hidden />
          ) : null}
        </div>
      ))}
    </div>,
    document.body,
  );
}
